import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  UserCog, Plus, Search, Edit, Trash2, Key, Shield, Percent,
  X, Save, Eye, EyeOff, Loader2, Check, Ban, FileText, Upload, PenTool
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// User Form Component
const UserForm = ({ user, businessId, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    position: '',
    role: 'vendedor',
    can_authorize_discounts: false,
    max_discount_percent: 0,
    can_authorize_po: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signatureImage, setSignatureImage] = useState('');
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const sigInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setSignatureImage(user.signature_image || '');
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        password: '', // Don't show password
        email: user.email || '',
        full_name: user.full_name || '',
        position: user.position || '',
        role: user.role || 'vendedor',
        can_authorize_discounts: user.can_authorize_discounts || false,
        max_discount_percent: user.max_discount_percent || 0,
        can_authorize_po: user.can_authorize_po || false
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user && !formData.password) {
      alert('La contraseña es requerida para nuevos usuarios');
      return;
    }
    
    setLoading(true);
    try {
      const url = user?.id 
        ? `${API}/api/users/${user.id}`
        : `${API}/api/users`;
      
      const method = user?.id ? 'PUT' : 'POST';
      
      // For updates, only send password if it was changed
      const payload = user?.id 
        ? {
            email: formData.email,
            full_name: formData.full_name,
            position: formData.position,
            role: formData.role,
            can_authorize_discounts: formData.can_authorize_discounts,
            max_discount_percent: formData.max_discount_percent,
            can_authorize_po: formData.can_authorize_po,
            ...(formData.password && { password: formData.password })
          }
        : {
            ...formData,
            business_id: businessId
          };
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      if (data.success) {
        onSave(data.user);
      } else {
        alert(data.detail || 'Error al guardar usuario');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <UserCog className="w-5 h-5 text-purple-400" />
          {user?.id ? 'Editar Usuario' : 'Nuevo Usuario'}
        </h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Credenciales */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate-300 border-b border-slate-700 pb-2 flex items-center gap-2">
            <Key className="w-4 h-4" />
            Credenciales
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Usuario *</label>
              <input
                type="text"
                required
                disabled={!!user?.id}
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none disabled:opacity-50"
                placeholder="nombre.usuario"
              />
              {user?.id && (
                <p className="text-xs text-slate-500 mt-1">El nombre de usuario no se puede cambiar</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                {user?.id ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required={!user?.id}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-3 py-2 pr-10 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Datos Personales */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate-300 border-b border-slate-700 pb-2">
            Datos Personales
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Nombre Completo</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Juan Pérez"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Correo Electrónico</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                placeholder="juan@empresa.com"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Cargo / Posición</label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData({...formData, position: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Ejecutivo de Ventas"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Rol</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              >
                <option value="vendedor">Vendedor</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
          </div>
        </div>

        {/* Permisos de Descuento */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate-300 border-b border-slate-700 pb-2 flex items-center gap-2">
            <Percent className="w-4 h-4" />
            Permisos de Descuento
          </h4>
          <div className="flex items-start gap-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.can_authorize_discounts}
                onChange={(e) => setFormData({
                  ...formData, 
                  can_authorize_discounts: e.target.checked,
                  max_discount_percent: e.target.checked ? formData.max_discount_percent : 0
                })}
                className="w-5 h-5 rounded border-slate-600 bg-slate-900 text-purple-500 focus:ring-purple-500"
              />
              <span className="text-white">Puede autorizar descuentos</span>
            </label>
            
            {formData.can_authorize_discounts && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-400">Máximo:</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={formData.max_discount_percent}
                  onChange={(e) => setFormData({...formData, max_discount_percent: parseFloat(e.target.value) || 0})}
                  className="w-20 px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm text-center focus:border-blue-500 focus:outline-none"
                />
                <span className="text-slate-400">%</span>
              </div>
            )}
          </div>
        </div>

        {/* Permisos de Órdenes de Compra */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate-300 border-b border-slate-700 pb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Permisos de Órdenes de Compra
          </h4>
          <div className="flex items-start gap-6">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="can-authorize-po-checkbox">
              <input
                type="checkbox"
                checked={formData.can_authorize_po}
                onChange={(e) => setFormData({...formData, can_authorize_po: e.target.checked})}
                className="w-5 h-5 rounded border-slate-600 bg-slate-900 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-white">Puede autorizar Órdenes de Compra</span>
            </label>
          </div>
          <p className="text-xs text-slate-500">
            Este usuario aparecerá en el dropdown "Autoriza" al crear órdenes de compra.
          </p>
        </div>

        {/* Firma Digital */}
        {user?.id && (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-slate-300 border-b border-slate-700 pb-2 flex items-center gap-2">
              <PenTool className="w-4 h-4" />
              Firma Digital
            </h4>
            <p className="text-xs text-slate-500">
              Sube una imagen de tu firma (PNG o JPG). Se usará en cotizaciones y órdenes de compra.
            </p>
            
            {signatureImage ? (
              <div className="flex items-center gap-4">
                <div className="bg-white rounded-lg p-3 inline-block">
                  <img src={signatureImage} alt="Firma" className="h-16 object-contain" />
                </div>
                <div className="flex gap-2">
                  <label className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 cursor-pointer flex items-center gap-1 text-sm">
                    <Upload className="w-4 h-4" />
                    Cambiar
                    <input ref={sigInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setUploadingSignature(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          const res = await axios.post(`${API}/api/users/${user.id}/signature`, formData);
                          if (res.data.success) {
                            setSignatureImage(res.data.signature_image);
                            alert('Firma actualizada');
                          }
                        } catch (err) {
                          alert('Error al subir firma');
                        } finally {
                          setUploadingSignature(false);
                          if (sigInputRef.current) sigInputRef.current.value = '';
                        }
                      }} />
                  </label>
                  <button type="button" onClick={async () => {
                    try {
                      await axios.delete(`${API}/api/users/${user.id}/signature`);
                      setSignatureImage('');
                      alert('Firma eliminada');
                    } catch (err) {
                      alert('Error al eliminar firma');
                    }
                  }} className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 flex items-center gap-1 text-sm">
                    <Trash2 className="w-4 h-4" /> Eliminar
                  </button>
                </div>
                {uploadingSignature && <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />}
              </div>
            ) : (
              <label className="flex items-center justify-center gap-3 p-6 border-2 border-dashed border-slate-600 rounded-lg hover:border-purple-500 cursor-pointer transition-colors">
                {uploadingSignature ? (
                  <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                ) : (
                  <PenTool className="w-6 h-6 text-slate-400" />
                )}
                <div>
                  <p className="text-white text-sm font-medium">Subir imagen de firma</p>
                  <p className="text-slate-400 text-xs">PNG, JPG (preferiblemente fondo transparente)</p>
                </div>
                <input ref={sigInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setUploadingSignature(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await axios.post(`${API}/api/users/${user.id}/signature`, formData);
                      if (res.data.success) {
                        setSignatureImage(res.data.signature_image);
                        alert('Firma subida correctamente');
                      }
                    } catch (err) {
                      alert('Error al subir firma');
                    } finally {
                      setUploadingSignature(false);
                      if (sigInputRef.current) sigInputRef.current.value = '';
                    }
                  }} />
              </label>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {user?.id ? 'Guardar Cambios' : 'Crear Usuario'}
          </button>
        </div>
      </form>
    </div>
  );
};

// User Card Component
const UserCard = ({ user, onEdit, onDelete, onToggleActive }) => {
  const isAdmin = user.role === 'admin';
  
  return (
    <div className={`bg-slate-800/30 rounded-lg border border-slate-700/50 p-4 ${!user.is_active ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            isAdmin ? 'bg-purple-500/20' : 'bg-blue-500/20'
          }`}>
            {isAdmin ? (
              <Shield className={`w-6 h-6 ${isAdmin ? 'text-purple-400' : 'text-blue-400'}`} />
            ) : (
              <UserCog className="w-6 h-6 text-blue-400" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium text-white">{user.full_name || user.username}</h4>
              <span className={`px-2 py-0.5 rounded text-xs ${
                isAdmin ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
              }`}>
                {isAdmin ? 'Admin' : 'Vendedor'}
              </span>
              {user.can_authorize_po && (
                <span className="px-2 py-0.5 rounded text-xs bg-orange-500/20 text-orange-400">
                  Autoriza OC
                </span>
              )}
              {!user.is_active && (
                <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">
                  Inactivo
                </span>
              )}
            </div>
            <p className="text-sm text-slate-400">@{user.username}</p>
            {user.position && (
              <p className="text-xs text-slate-500">{user.position}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {user.can_authorize_discounts && (
            <div className="text-right">
              <p className="text-xs text-slate-500">Descuento máx.</p>
              <p className="text-sm text-orange-400 font-medium">{user.max_discount_percent}%</p>
            </div>
          )}
          
          <div className="flex items-center gap-1">
            <button
              onClick={() => onToggleActive(user)}
              className={`p-2 rounded-lg transition-colors ${
                user.is_active 
                  ? 'text-slate-400 hover:bg-slate-700' 
                  : 'text-green-400 hover:bg-green-500/20'
              }`}
              title={user.is_active ? 'Desactivar' : 'Activar'}
            >
              {user.is_active ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
            </button>
            <button
              onClick={() => onEdit(user)}
              className="p-2 text-slate-400 hover:bg-slate-700 rounded-lg transition-colors"
              title="Editar"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(user)}
              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
              title="Eliminar"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Users Tab Component
export default function UsersTab({ businessId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/users?business_id=${businessId}`);
      const data = await res.json();
      if (data.success) {
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSave = (user) => {
    if (editingUser) {
      setUsers(prev => prev.map(u => u.id === user.id ? user : u));
    } else {
      setUsers(prev => [user, ...prev]);
    }
    setShowForm(false);
    setEditingUser(null);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`¿Eliminar usuario "${user.username}"?`)) return;
    
    try {
      const res = await fetch(`${API}/api/users/${user.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setUsers(prev => prev.filter(u => u.id !== user.id));
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const res = await fetch(`${API}/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !user.is_active })
      });
      const data = await res.json();
      if (data.success) {
        setUsers(prev => prev.map(u => u.id === user.id ? data.user : u));
      }
    } catch (error) {
      console.error('Error toggling user active:', error);
    }
  };

  const filteredUsers = users.filter(u => 
    u.username?.toLowerCase().includes(search.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const adminCount = users.filter(u => u.role === 'admin').length;
  const activeCount = users.filter(u => u.is_active).length;

  return (
    <div className="space-y-6" data-testid="users-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <UserCog className="w-7 h-7 text-purple-400" />
            Usuarios del Sistema
          </h2>
          <p className="text-slate-400 mt-1">
            {users.length} usuario{users.length !== 1 ? 's' : ''} ({adminCount} admin{adminCount !== 1 ? 's' : ''}, {activeCount} activo{activeCount !== 1 ? 's' : ''})
          </p>
        </div>
        <button
          onClick={() => { setEditingUser(null); setShowForm(true); }}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors"
          data-testid="add-user-btn"
        >
          <Plus className="w-5 h-5" />
          Nuevo Usuario
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Buscar por nombre, usuario o correo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-purple-500 focus:outline-none"
          data-testid="search-users"
        />
      </div>

      {/* Form */}
      {showForm && (
        <UserForm
          user={editingUser}
          businessId={businessId}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingUser(null); }}
        />
      )}

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <UserCog className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{search ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}</p>
          {!search && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 text-purple-400 hover:text-purple-300"
            >
              Crear primer usuario
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredUsers.map(user => (
            <UserCard
              key={user.id}
              user={user}
              onEdit={(u) => { setEditingUser(u); setShowForm(true); }}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
            />
          ))}
        </div>
      )}
    </div>
  );
}
